import clsx from 'clsx';
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import { ComponentPropsWithoutRef, createContext, ReactNode, useContext, useState } from 'react';

const defaultScrollContainerElement = 'div';
type AvoAccordianProps<T extends React.ElementType = typeof defaultScrollContainerElement> =
  ComponentPropsWithoutRef<T> & {
    as?: T;
  };

interface AvoAccordianTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode | ((props: { isOpen: boolean }) => ReactNode);
}

interface AvoAccordianPanelProps
  extends Omit<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'exit' | 'transition'> {
  children: ReactNode;
}

interface AvoAccordianContextProps {
  isOpen: boolean;
  toggle: () => void;
}

const AvoAccordianContext = createContext<AvoAccordianContextProps | undefined>(undefined);

export const AvoAccordian = <T extends React.ElementType = typeof defaultScrollContainerElement>({
  as,
  ...props
}: AvoAccordianProps<T>) => {
  const Component = as ?? defaultScrollContainerElement;
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <AvoAccordianContext.Provider value={{ isOpen, toggle }}>
      <Component {...props} />
    </AvoAccordianContext.Provider>
  );
};

export const AvoAccordianTrigger = ({
  className,
  children,
  ...props
}: AvoAccordianTriggerProps) => {
  const context = useContext(AvoAccordianContext);
  const { isOpen } = context || { isOpen: false };
  if (!context) {
    throw new Error('AvoAccordianTrigger must be used within an AvoAccordian');
  }

  return (
    <div onClick={context.toggle} className={clsx('cursor-pointer', className)} {...props}>
      {typeof children === 'function' ? children({ isOpen }) : children}
    </div>
  );
};

export const AvoAccordianPanel = ({ className, ...props }: AvoAccordianPanelProps) => {
  const duration = 0.15;
  const context = useContext(AvoAccordianContext);
  if (!context) {
    throw new Error('AvoAccordianPanel must be used within an AvoAccordian');
  }

  return (
    <AnimatePresence>
      {context.isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            duration: duration,
            opacity: { duration: duration, ease: [0.42, 0, 0.58, 1] },
          }}
          className={clsx('overflow-hidden', className)}
          {...props}
        />
      )}
    </AnimatePresence>
  );
};
